import React from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { CommunityLocation } from '../types/api';
import { careColors } from '../utils/careColors';
import {
  Groups,
  CalendarMonth,
  Star
} from '@mui/icons-material';

interface MapMarkerProps {
  location: CommunityLocation;
  isHighlighted: boolean;
  isDimmed: boolean;
  onClick: (location: CommunityLocation) => void;
}

// Function to get marker icon based on care level with optional highlighting
const getMarkerIcon = (careLevel: string | string[], isHighlighted: boolean = false, isDimmed: boolean = false): L.DivIcon => {
  // Use a fixed dark blue color for all markers
  const baseColor = '#3f3f3fff';
  
  let backgroundColor = baseColor;
  let borderColor = 'white';
  let borderWidth = 2;
  let width = 20;
  let height = 20;
  let opacity = isDimmed ? 0.3 : 1;
  let boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
  
  if (isHighlighted) {
    width = 28;
    height = 28;
    borderWidth = 3;
    borderColor = '#FFD700'; // Gold border for highlight
    boxShadow = '0 4px 8px rgba(0,0,0,0.5), 0 0 12px rgba(255, 215, 0, 0.6)';
    opacity = 1;
  }
  
  return L.divIcon({
    html: `<div style="
      background-color: ${backgroundColor};
      width: ${width}px;
      height: ${height}px;
      border-radius: 50%;
      border: ${borderWidth}px solid ${borderColor};
      box-shadow: ${boxShadow};
      opacity: ${opacity};
      transition: all 0.2s ease;
    "></div>`,
    className: 'custom-marker',
    iconSize: [width, height],
    iconAnchor: [width/2, height/2],
  });
};

const MapMarker: React.FC<MapMarkerProps> = ({
  location,
  isHighlighted,
  isDimmed,
  onClick
}) => {
  return (
    <Marker
      position={[location.latitude, location.longitude]}
      icon={getMarkerIcon(location.care_level || '', isHighlighted, isDimmed)}
      eventHandlers={{
        click: () => onClick(location),
      }}
    >
      <Tooltip permanent={false} direction="top" offset={[0, -10]}>
        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {location.community_name}
          </div>
          {location.corporation_name && (
            <div style={{ color: '#666' }}>
              {location.corporation_name}
            </div>
          )}
          {location.care_level && (
            <div style={{ color: '#888', fontSize: '11px' }}>
              {
                location.care_level.map(level => typeof level === 'string' ? level.toUpperCase() : level).join(', ')
              }
            </div>
          )}
          {location.resident_count && (
            <div style={{ color: '#888', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Groups fontSize="inherit" /> {location.resident_count} residents
            </div>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
};

export default MapMarker;
